import React, { useCallback, useState, useMemo, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { Input, Typography, Space, Card, Button, InputNumber, Switch, Tag, Avatar, Checkbox, Alert, Form, Select } from 'antd'
import { PlayCircleOutlined, PlusOutlined, MinusOutlined } from '@ant-design/icons'
import { StepComponentProps } from '../index'
import { OnboardingV2Data } from '../index'
import { countries, findCountry, ICountry, IPlace, placeLabel, countriesForPicker, subdivisionOptions, subdivisionTermKey } from '@vacationtracker/shared/data/countries'
import { filterCountryOptions } from '@vacationtracker/shared/functions/filter-countries'
import { filterOptions } from '../../../util/filter-options'
import { leaveLawPolicyForLeaveType } from '@vacationtracker/shared/functions/leave-laws'
import { useLeaveTypesApi } from '../../../services/api/leave-type-templates-hook'
import { ILeavePolicy } from '../../../types/locations'
import IntlMessages from '../../../util/IntlMessages'
import Api from '@vacationtracker/shared/services/api'
import { PublicHolidaysRepository } from '@vacationtracker/shared/common/date-holidays-repository'
import { useOnboardingContext, useMarkStepCompleted } from '../context'
import { ShortestLeaveIntervalEnum } from '@vacationtracker/shared/types/leave-policy'
import logger from '@vacationtracker/shared/functions/logger'
import { track } from '../../../services/analytics/analytics'
import { useAppSelector } from '../../../store/hooks'
import { selectAuthCompanySlice } from '../../../store/auth-company-slice'
import { IHeatmapHoliday, HeatmapEventType } from '../../heatmap-chart/types'
import { IGetLeaveTypeList } from '../../../types/locations'
import { SubscriptionPlanEnum } from '@vacationtracker/shared/types/company'
import { placeChangeImportEvent } from './location-setup-events'

const { Title, Text } = Typography

interface LocationSetupData extends OnboardingV2Data {
  country?: ICountry
  leavePolicies?: ILeavePolicy[]
}

export const LocationSetupStep: React.FC<StepComponentProps<LocationSetupData>> = ({ onNext, currentStepIndex }) => {
  const { formatMessage } = useIntl()
  const { dispatch: updateData, formData: data, isLoading } = useOnboardingContext()
  const markStepCompleted = useMarkStepCompleted()
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const [showLocationEdit, setShowLocationEdit] = useState(false)
  // Signup already stored a place on the default location, so start from it rather than making
  // the admin re-pick what they just told us.
  const signupCountry = findCountry(data.authUser?.location?.countryCode ?? '')
  const [selectedCountry, setSelectedCountry] = useState<ICountry | null>(signupCountry ?? null)
  const [selectedStateIso, setSelectedStateIso] = useState<string | null>(data.authUser?.location?.subdivisionCode ?? null)
  const country = data.country || signupCountry || countries.find((c) => c.name === data.authUser?.location?.name)
  const placeChanged =
    selectedCountry?.iso !== (data.authUser?.location?.countryCode || undefined) || (selectedStateIso ?? null) !== (data.authUser?.location?.subdivisionCode ?? null)
  const subdivisions = selectedCountry ? subdivisionOptions(selectedCountry.iso) : []

  // Leave laws are an enrichment: if the feed is unavailable the query simply has no data and the
  // policies keep whatever signup gave them.
  const { data: leaveLaws } = useLeaveTypesApi()

  /**
   * The statutory policy for one of our default leave types under the currently selected place.
   * Matched on the feed's stable key, never on the display name - it calls it "Sick Leave" while
   * we call it "Sick Day".
   */
  const statutoryPolicyFor = useCallback(
    (leaveTypeName: string | undefined, place: IPlace) => leaveLawPolicyForLeaveType({ laws: leaveLaws, place, leaveTypeName, plan: authCompany?.plan }),
    [leaveLaws, authCompany?.plan]
  )

  /**
   * Keep the name in step with the chosen place, but only while it is still empty, still the name
   * signup gave it, or still the one we last generated. Anything the admin has typed is theirs and
   * is never overwritten.
   */
  const nextLocationName = (nextCountry: ICountry | null, nextSubdivision: string | null): string | undefined => {
    const untouched = new Set(['', data.authUser?.location?.name ?? '', selectedCountry ? placeLabel({ countryCode: selectedCountry.iso, subdivisionCode: selectedStateIso }) : ''])
    if (!untouched.has(String(data.locationName ?? ''))) return undefined

    return nextCountry ? placeLabel({ countryCode: nextCountry.iso, subdivisionCode: nextSubdivision }) : undefined
  }

  const onCountryChange = (value: string) => {
    const country = findCountry(value) ?? null
    const locationName = nextLocationName(country, null)
    setSelectedCountry(country)
    setSelectedStateIso(null)

    if (country) {
      if (authCompany) {
        track('ONBOARDING_STEP_1_LOCATION_CHANGED', {
          previous_location: data.locationName,
          new_location: country.name,
          company_id: authCompany.id,
        })
      }
      updateData({
        type: 'UPDATE_FORM_DATA',
        payload: { country, ...(locationName !== undefined && { locationName }) },
      })
      applyStatutoryPolicies(country.iso, null)
    }
  }

  const onStateChange = (value: string) => {
    const subdivision = value || null
    const locationName = nextLocationName(selectedCountry, subdivision)
    setSelectedStateIso(subdivision)

    if (locationName !== undefined) {
      updateData({ type: 'UPDATE_FORM_DATA', payload: { locationName } })
    }
    applyStatutoryPolicies(selectedCountry?.iso, subdivision)
  }
  const leavePolicies = useMemo(() => {
    const policies = data.leavePolicies || data.authUser?.location?.leavePolicies || []
    const priorityOrder = ['Paid Time Off', 'Sick Day', 'Working Remotely']

    return [...policies].sort((a, b) => {
      const aName = a.leaveType?.name || ''
      const bName = b.leaveType?.name || ''
      const aIndex = priorityOrder.indexOf(aName)
      const bIndex = priorityOrder.indexOf(bName)

      // If both are in priority order, sort by their priority
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex
      }

      // If only a is in priority order, it comes first
      if (aIndex !== -1) {
        return -1
      }

      // If only b is in priority order, it comes first
      if (bIndex !== -1) {
        return 1
      }

      // If neither is in priority order, sort alphabetically
      return aName.localeCompare(bName)
    })
  }, [data.leavePolicies, data.authUser?.location?.leavePolicies])

  /**
   * Re-seed the previewed quotas from the law of a newly chosen place.
   *
   * Applied here rather than at submit so the cards show what will actually be saved, and so a
   * quota the admin types afterwards is the one that sticks. Hour-based companies are skipped:
   * the law speaks in days and hoursPerYear is what counts for them.
   */
  const applyStatutoryPolicies = (countryIso: string | undefined, subdivisionCode: string | null) => {
    if (!countryIso) return

    // Each policy restarts from what the location was saved with, so switching to a country the
    // feed does not cover falls back instead of keeping the previous country's statutory numbers.
    const saved = data.authUser?.location?.leavePolicies ?? []

    const next = leavePolicies.map((policy) => {
      if (policy.quotaSetIn === 'hours') return policy

      const base = saved.find((savedPolicy) => savedPolicy.leaveType?.id === policy.leaveType?.id) ?? policy
      const law = statutoryPolicyFor(policy.leaveType?.name, { countryCode: countryIso, subdivisionCode })
      return { ...policy, ...base, ...law }
    }) as ILeavePolicy[]

    updateData({ type: 'UPDATE_FORM_DATA', payload: { leavePolicies: next } })
  }

  const handleNext = useCallback(async () => {
    if (!data.locationName || !onNext || !data.authUser) return

    try {
      updateData({ type: 'SET_LOADING', payload: true })

      if (authCompany) {
        const leaveTypesConfigured: string[] = (leavePolicies || []).filter((p) => p.isActive).map((p) => p.leaveType?.name || 'Unknown')
        track('ONBOARDING_STEP_1_CONTINUE_CLICKED', {
          company_id: authCompany.id,
          user_id: data.authUser.id,
          location_name: data.locationName,
          leave_types_configured: leaveTypesConfigured,
        })
      }

      // Make LOCATION_UPDATED API call
      const locationResponse = await Api.post('/core/event', {
        eventType: 'LOCATION_UPDATED',
        eventGroup: 'LOCATION',
        users: [data.authUser?.id],
        timezone: data.authUser?.location?.timezone,
        locationId: data.authUser?.location?.id,
        name: data.locationName,
        countryCode: selectedCountry?.iso,
        // Explicit null, not undefined: the update handler only overwrites keys the event carries,
        // so undefined would leave a stale subdivision behind after changing country.
        subdivisionCode: selectedStateIso ?? null,
        resetQuotas: data.authUser?.location?.resetQuotas || 'FISCAL_YEAR',
        isDefault: data.authUser?.location?.isDefault || true,
        firstDayOfWeek: data.authUser?.location?.firstDayOfWeek || 1,
        yearStartDay: data.authUser?.location?.yearStartDay || 1,
        yearStartMonth: data.authUser?.location?.yearStartMonth || 1,
        platform: data.authUser?.platform,
      })

      // If user changed the location (country/state), import location holidays for the current year.
      // The place is pre-filled from signup, so this has to compare against it - otherwise simply
      // clicking through the step would re-import holidays that are already there.
      if (selectedCountry && placeChanged && data.authUser?.location?.id) {
        const currentYear = new Date().getFullYear()
        const locale = (data.authUser as any)?.locale || 'en-US'
        try {
          const publicHolidaysRepository = new PublicHolidaysRepository(locale)
          const publicHolidays = publicHolidaysRepository.getHolidaysForCountry(currentYear, selectedCountry.iso, selectedStateIso || undefined, 'public')
          const holidays = (publicHolidays || []).map((h) => ({ name: h.name, date: h.date.slice(0, 10) }))
          // Update onboarding context immediately to avoid UI staleness/race with network
          const heatmapHolidays: IHeatmapHoliday[] = holidays.map((h) => ({
            id: `${data.authUser!.location!.id}-${h.date}-${h.name}`,
            type: 'LIB',
            heatmapType: HeatmapEventType.holiday,
            name: h.name,
            date: h.date,
            locationId: data.authUser!.location!.id,
            locationName: data.locationName || selectedCountry.name || data.authUser!.location!.name,
          }))
          updateData({ type: 'UPDATE_FORM_DATA', payload: { holidays: heatmapHolidays } })
          await Api.post(
            '/core/event',
            placeChangeImportEvent({
              locationId: data.authUser.location.id,
              holidays,
              year: currentYear,
              country: selectedCountry.iso,
              state: selectedStateIso || undefined,
              locale,
            })
          )
        } catch (e) {
          logger.error('Failed to import holidays for updated location', e)
        }
      }

      // Handle leave types and policies
      for (const policy of leavePolicies) {
        const shouldBeActive = policy.isActive && policy.leaveType?.isActive
        logger.info('Configuring leave type and policy:', policy.leaveType?.name, { shouldBeActive })

        // First, update the leave type's active state
        await Api.post('/core/event', {
          eventType: 'LEAVE_TYPE_UPDATED',
          eventGroup: 'LEAVE_TYPE',
          leaveTypeId: policy.leaveType?.id,
          name: policy.leaveType?.name,
          color: (policy.leaveType as IGetLeaveTypeList)?.color || '#7f00ff', // Cast to IGetLeaveTypeList to get color
          isActive: shouldBeActive,
          platform: data.authUser?.platform,
        })

        if (shouldBeActive) {
          // For active policies, send LEAVE_POLICY_ENABLED to update settings
          await Api.post('/core/event', {
            toil: policy.toil || false,
            eventType: 'LEAVE_POLICY_ENABLED',
            eventGroup: 'LEAVE_POLICY',
            leaveTypeId: policy.leaveType?.id,
            locationId: data.authUser?.location?.id,
            daysPerYear: policy.daysPerYear,
            hoursPerYear: policy.hoursPerYear,
            quotaSetIn: policy.quotaSetIn || 'days',
            maxRolloverDays: policy.maxRolloverDays || 0,
            maxRolloverHours: policy.maxRolloverHours || 0,
            hasUnlimitedDays: policy.hasUnlimitedDays || false,
            shortestLeaveInterval: policy.shortestLeaveInterval || ShortestLeaveIntervalEnum.fullDay,
            hideLeaveType: policy.hideLeaveType || false,
            accrualType: policy.accrualType || 'NONE',
            isApprovalRequired: policy.isApprovalRequired,
            isReasonRequired: policy.isReasonRequired || false,
            allowLeaveInPast: policy.allowLeaveInPast || false,
            broughtForwardExpirationSettings: policy.broughtForwardExpirationSettings || { enabled: false },
            allowAdvanceAccrualUsage: policy.allowAdvanceAccrualUsage || false,
            platform: data.authUser?.platform,
            negativeBroughtForwardAllowed: false,
            version: 3,
          })
        } else {
          // For inactive policies, disable them
          logger.info('Disabling leave policy:', policy.leaveType?.name)
          await Api.post('/core/event', {
            eventType: 'LEAVE_POLICY_DISABLED',
            eventGroup: 'LEAVE_POLICY',
            leaveTypeId: policy.leaveType?.id,
            locationId: data.authUser?.location?.id,
            platform: data.authUser?.platform,
          })
        }
      }

      // Mark step as completed (track relevant steps only)
      markStepCompleted('location-setup')

      await onNext()
    } catch (error) {
      logger.error('Error saving location and leave policies:', error)
      // TODO: Handle error display to user
    } finally {
      updateData({ type: 'SET_LOADING', payload: false })
    }
  }, [data.locationName, onNext, data.authUser, leavePolicies, updateData, markStepCompleted, selectedCountry, selectedStateIso])

  const handlePolicyChange = (index: number, changes: Partial<ILeavePolicy>) => {
    const newPolicies = [...leavePolicies]
    newPolicies[index] = { ...(newPolicies[index] as ILeavePolicy), ...changes }
    updateData({ type: 'UPDATE_FORM_DATA', payload: { leavePolicies: newPolicies as ILeavePolicy[] } })

    // Track leave type modifications
    const policy = newPolicies[index]
    if (authCompany && policy?.leaveType?.name) {
      if (Object.prototype.hasOwnProperty.call(changes, 'isActive')) {
        track('ONBOARDING_STEP_1_LEAVE_TYPE_MODIFIED', {
          leave_type: policy.leaveType.name,
          field_changed: 'toggle',
          new_value: policy.isActive,
          company_id: authCompany.id,
        })
      }
      if (Object.prototype.hasOwnProperty.call(changes, 'daysPerYear')) {
        track('ONBOARDING_STEP_1_LEAVE_TYPE_MODIFIED', {
          leave_type: policy.leaveType.name,
          field_changed: 'value',
          new_value: policy.daysPerYear,
          company_id: authCompany.id,
        })
      }
      if (Object.prototype.hasOwnProperty.call(changes, 'hasUnlimitedDays')) {
        track('ONBOARDING_STEP_1_LEAVE_TYPE_MODIFIED', {
          leave_type: policy.leaveType.name,
          field_changed: 'unlimited',
          new_value: policy.hasUnlimitedDays,
          company_id: authCompany.id,
        })
      }
    }
  }

  const handleLeaveTypeActiveChange = (index: number, isActive: boolean) => {
    const newPolicies = [...leavePolicies]
    const current = newPolicies[index]
    if (!current) return
    const updated = {
      ...current,
      isActive, // Update policy.isActive
      leaveType: {
        ...current.leaveType,
        isActive, // Update leaveType.isActive
      },
    } as ILeavePolicy
    newPolicies[index] = updated
    updateData({ type: 'UPDATE_FORM_DATA', payload: { leavePolicies: newPolicies as ILeavePolicy[] } })
  }

  // Screen shown
  useEffect(() => {
    if (authCompany && data?.authUser) {
      track('ONBOARDING_STEP_1_LOCATION_SHOWN', {
        company_id: authCompany.id,
        user_id: data.authUser.id,
      })
    }
  }, [authCompany, data?.authUser])

  const handleClickChangeCountry = () => {
    setShowLocationEdit(true)
  }

  return (
    <div className="location-setup-step">
      <div className="step-content">
        <div className="step-left-section">
          <div>
            <div style={{ marginBottom: 40 }}>
              <Text type="secondary" style={{ fontSize: 14, display: 'block', marginBottom: 24 }}>
                <IntlMessages id="onboarding.location.stepIndicator" values={{ current: 1 }} />
              </Text>
              <Title level={2} style={{ fontSize: 32, marginBottom: 16 }}>
                <IntlMessages id="onboarding.location.title" />
              </Title>
              <Space align="center" size={4}>
                <Text style={{ color: '#666' }}>
                  <IntlMessages id="onboarding.location.countryMention" values={{ countryName: country?.name }} />
                </Text>
                <Button type="link" onClick={handleClickChangeCountry} style={{ padding: 0 }}>
                  <IntlMessages id="onboarding.location.changeCountry" />
                </Button>
              </Space>
            </div>

            <div style={{ marginBottom: 40 }}>
              <Form layout="vertical">
                {showLocationEdit && (
                  <>
                    {/* Controlled by local state rather than bound by `name`: this Form is never
                        submitted, and a name-bound value would not reset when the country changes. */}
                    <Form.Item className="wrap-label" label={<IntlMessages id="app.country" />} required>
                      <Select value={selectedCountry?.iso} onChange={onCountryChange} filterOption={filterCountryOptions} className="data-hj-allow" showSearch>
                        {countriesForPicker().map((country) => (
                          <Select.Option value={country.iso} key={country.iso}>
                            {country.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                    {subdivisions.length > 0 && (
                      <Form.Item className="wrap-label" label={<IntlMessages id={subdivisionTermKey(selectedCountry?.iso ?? '')} />}>
                        <Select
                          value={selectedStateIso ?? undefined}
                          filterOption={filterOptions}
                          className="data-hj-allow"
                          showSearch
                          allowClear
                          placeholder={formatMessage({ id: 'locationPlace.subdivision.placeholder' })}
                          onChange={onStateChange}
                        >
                          {subdivisions.map((subdivision) => (
                            <Select.Option value={subdivision.value} key={subdivision.value}>
                              {subdivision.label}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    )}
                  </>
                )}
                <Text style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                  <IntlMessages id="onboarding.location.nameLabel" />
                </Text>
                <Input
                  placeholder="Location Name"
                  size="large"
                  value={data.locationName}
                  onChange={(e) => updateData({ type: 'UPDATE_FORM_DATA', payload: { locationName: e.target.value } })}
                  readOnly={!showLocationEdit}
                />
              </Form>
            </div>

            {/* Mobile Location Preview Card */}
            <div className="mobile-location-preview" style={{ marginBottom: 40 }}>
              <Card
                className="location-preview-card"
                styles={{
                  body: { padding: 24 },
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24, gap: 12 }}>
                  <Space>
                    <Avatar src={`http://purecatamphetamine.github.io/country-flag-icons/3x2/${selectedCountry?.iso || country?.iso}.svg`} size={32} shape="circle" />
                    <Text strong>{data.locationName || 'Location Name'}</Text>
                  </Space>
                  <Tag color="#f0e6ff" style={{ marginLeft: 'auto', color: '#7F00FF', border: 'none' }}>
                    <IntlMessages id="onboarding.location.preview.default" />
                  </Tag>
                </div>

                <Space direction="vertical" size={24} style={{ width: '100%' }}>
                  {leavePolicies.map((policy, index) => (
                    <div key={policy.leaveType?.name}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                        <Text strong>{policy.leaveType?.name}</Text>
                        {leavePolicies.length > 1 ? (
                          <Switch checked={policy.leaveType?.isActive ?? false} onChange={(checked) => handleLeaveTypeActiveChange(index, checked)} />
                        ) : null}
                      </div>

                      {policy.leaveType?.isActive && (
                        <div>
                          {!policy.hasUnlimitedDays ? (
                            <InputNumber
                              addonBefore={
                                <MinusOutlined
                                  onClick={() => handlePolicyChange(index, { daysPerYear: Math.max(0, policy.daysPerYear - 1) })}
                                  style={{
                                    cursor: policy.daysPerYear <= 0 ? 'not-allowed' : 'pointer',
                                    color: policy.daysPerYear <= 0 ? '#d9d9d9' : '#666',
                                    fontSize: 16,
                                  }}
                                />
                              }
                              addonAfter={
                                <PlusOutlined
                                  onClick={() => handlePolicyChange(index, { daysPerYear: (policy.daysPerYear as number) + 1 })}
                                  style={{
                                    cursor: 'pointer',
                                    color: '#666',
                                    fontSize: 16,
                                  }}
                                />
                              }
                              value={policy.daysPerYear}
                              controls={false}
                              onChange={(value) => handlePolicyChange(index, { daysPerYear: value || 0 })}
                              style={{
                                width: '100%',
                              }}
                              min={0}
                            />
                          ) : null}
                          <div style={{ marginTop: 8 }}>
                            <Space align="center">
                              <Checkbox checked={policy.hasUnlimitedDays} onChange={(e) => handlePolicyChange(index, { hasUnlimitedDays: e.target.checked })} />
                              <Text type="secondary" style={{ fontSize: 13 }}>
                                <IntlMessages id="onboarding.location.preview.unlimited" />
                              </Text>
                            </Space>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </Space>
              </Card>

              <Alert
                message={<IntlMessages id="onboarding.location.preview.holidays.title" />}
                description={
                  authCompany?.plan === SubscriptionPlanEnum.freemium ? (
                    <IntlMessages id="onboarding.location.preview.holidays.freemium.description" />
                  ) : (
                    <IntlMessages id="onboarding.location.preview.holidays.description" />
                  )
                }
                type="info"
                showIcon
                style={{ marginTop: 16 }}
              />
            </div>

            <div style={{ marginBottom: 40 }}>
              <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
                <IntlMessages id="onboarding.location.policiesNote" />
              </Text>

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  type="primary"
                  size="large"
                  onClick={handleNext}
                  disabled={!data.locationName}
                  loading={isLoading}
                  style={{ background: '#7F00FF', borderColor: '#7F00FF', height: 48, paddingInline: 32 }}
                >
                  <IntlMessages id="onboarding.location.continue" />
                </Button>
              </div>
            </div>
            <Card className="onboarding-video-card">
              <Space align="start" size={16}>
                <a
                  href="https://www.youtube.com/watch?v=b97xMkrIXR0"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open locations video"
                  onClick={() => authCompany && track('ONBOARDING_STEP_1_VIDEO_TUTORIAL_OPENED', { company_id: authCompany.id })}
                  style={{
                    display: 'block',
                    width: 96,
                    height: 54,
                    borderRadius: 8,
                    overflow: 'hidden',
                    position: 'relative',
                    flexShrink: 0,
                  }}
                >
                  <img
                    src="https://img.youtube.com/vi/b97xMkrIXR0/hqdefault.jpg"
                    alt="Locations video thumbnail"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                  <PlayCircleOutlined
                    style={{
                      position: 'absolute',
                      left: '50%',
                      top: '50%',
                      transform: 'translate(-50%, -50%)',
                      fontSize: 28,
                      color: '#ffffff',
                      textShadow: '0 2px 6px rgba(0,0,0,0.6)',
                    }}
                  />
                </a>
                <div>
                  <Text strong style={{ fontSize: 16, display: 'block', marginBottom: 4 }}>
                    <IntlMessages id="onboarding.location.about.title" />
                  </Text>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                    <IntlMessages id="onboarding.location.about.description" />
                  </Text>
                  <Button type="link" style={{ padding: 0 }} onClick={() => window.open('https://www.youtube.com/watch?v=b97xMkrIXR0', '_blank', 'noopener')}>
                    <IntlMessages id="onboarding.location.about.learnMore" />
                  </Button>
                </div>
              </Space>
            </Card>
          </div>
        </div>

        <div className="step-right-section">
          <div>
            <Card
              className="location-preview-card"
              styles={{
                body: { padding: 24 },
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24, gap: 12 }}>
                <Space>
                  <Avatar src={`http://purecatamphetamine.github.io/country-flag-icons/3x2/${selectedCountry?.iso || country?.iso}.svg`} size={32} shape="circle" />
                  <Text strong>{data.locationName || 'Location Name'}</Text>
                </Space>
                <Tag color="#f0e6ff" style={{ marginLeft: 'auto', color: '#7F00FF', border: 'none' }}>
                  <IntlMessages id="onboarding.location.preview.default" />
                </Tag>
              </div>

              <Space direction="vertical" size={24} style={{ width: '100%' }}>
                {leavePolicies.map((policy, index) => (
                  <div key={policy.leaveType?.name}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                      <Text strong>{policy.leaveType?.name}</Text>
                      {leavePolicies.length > 1 ? (
                        <Switch checked={policy.leaveType?.isActive ?? false} onChange={(checked) => handleLeaveTypeActiveChange(index, checked)} />
                      ) : null}
                    </div>

                    {policy.leaveType?.isActive && (
                      <div>
                        {!policy.hasUnlimitedDays ? (
                          <InputNumber
                            addonBefore={
                              <MinusOutlined
                                onClick={() => handlePolicyChange(index, { daysPerYear: Math.max(0, policy.daysPerYear - 1) })}
                                style={{
                                  cursor: policy.daysPerYear <= 0 ? 'not-allowed' : 'pointer',
                                  color: policy.daysPerYear <= 0 ? '#d9d9d9' : '#666',
                                  fontSize: 16,
                                }}
                              />
                            }
                            addonAfter={
                              <PlusOutlined
                                onClick={() => handlePolicyChange(index, { daysPerYear: (policy.daysPerYear as number) + 1 })}
                                style={{
                                  cursor: 'pointer',
                                  color: '#666',
                                  fontSize: 16,
                                }}
                              />
                            }
                            value={policy.daysPerYear}
                            controls={false}
                            onChange={(value) => handlePolicyChange(index, { daysPerYear: value || 0 })}
                            style={{
                              width: '100%',
                            }}
                            min={0}
                          />
                        ) : null}
                        <div style={{ marginTop: 8 }}>
                          <Space align="center">
                            <Checkbox checked={policy.hasUnlimitedDays} onChange={(e) => handlePolicyChange(index, { hasUnlimitedDays: e.target.checked })} />
                            <Text type="secondary" style={{ fontSize: 13 }}>
                              <IntlMessages id="onboarding.location.preview.unlimited" />
                            </Text>
                          </Space>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </Space>
            </Card>

            <Alert
              message={<IntlMessages id="onboarding.location.preview.holidays.title" />}
              description={
                authCompany?.plan === SubscriptionPlanEnum.freemium ? (
                  <IntlMessages id="onboarding.location.preview.holidays.freemium.description" />
                ) : (
                  <IntlMessages id="onboarding.location.preview.holidays.description" />
                )
              }
              type="info"
              showIcon
              style={{ marginTop: 16 }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
